/**
 * SEO Meta Tag Utilities
 * Following the Epic Stack pattern for meta tag generation
 */

interface SeoMetaOptions {
	title: string
	description: string
	keywords?: string
	url?: string
	image?: string
	imageAlt?: string
	noindex?: boolean
}

const BASE_URL = 'https://indieworld.dev'
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`
const TWITTER_HANDLE = '@indieworldmap'

/**
 * Generate comprehensive SEO meta tags
 * Returns an array of meta tag objects compatible with React Router's MetaFunction
 *
 * @example
 * export const meta: MetaFunction = () => {
 *   return getSeoMeta({
 *     title: "Page Title — Indie World",
 *     description: "Page description for SEO",
 *     keywords: "keyword1, keyword2, keyword3",
 *     url: "https://indieworld.dev/page",
 *   })
 * }
 */
export function getSeoMeta({
	title,
	description,
	keywords,
	url,
	image = DEFAULT_IMAGE,
	imageAlt = 'Indie World - The Indie Hacker Map',
	noindex = false,
}: SeoMetaOptions) {
	const metaTags: Array<
		| { title: string }
		| { name: string; content: string }
		| { property: string; content: string }
	> = [
		// Basic meta tags
		{ title },
		{ name: 'description', content: description },

		// Open Graph tags
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:image', content: image },
		{ property: 'og:image:width', content: '1200' },
		{ property: 'og:image:height', content: '630' },
		{ property: 'og:image:alt', content: imageAlt },

		// Twitter Card tags
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:site', content: TWITTER_HANDLE },
		{ name: 'twitter:creator', content: TWITTER_HANDLE },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:image', content: image },
		{ name: 'twitter:image:alt', content: imageAlt },
	]

	// Add URL if provided (for canonical and og:url)
	if (url) {
		metaTags.push({ property: 'og:url', content: url })
	}

	// Add keywords if provided
	if (keywords) {
		metaTags.push({ name: 'keywords', content: keywords })
	}

	// Add noindex for private/error pages
	if (noindex) {
		metaTags.push({ name: 'robots', content: 'noindex' })
	}

	return metaTags
}
